import React, { useCallback, useRef, useState, useEffect } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { fromEvent, animationFrameScheduler } from 'rxjs';
import { switchMap, first, map, takeUntil, finalize, subscribeOn } from 'rxjs/operators';
import { isPromise } from '@tencent/workbench-core';
import { DragWrapper } from './drag-wrapper.js';
import { setActionPointEvent } from '../../providers/action-point/action-point-runner.js';

function useDrag(options) {
    const { onDragStart, onDragEnd, onDrag, dragOptions, enable = true, } = options;
    const { capture = true, preventDefault = false, stopPropagation = false, offset, } = dragOptions !== null && dragOptions !== void 0 ? dragOptions : {};
    const { x: offsetX = 0, y: offsetY = 0, } = offset !== null && offset !== void 0 ? offset : {};
    const handleEvent = useCallback((e) => {
        if (preventDefault) {
            e.preventDefault();
        }
        if (stopPropagation) {
            e.stopPropagation();
        }
    }, [preventDefault, stopPropagation]);
    const wrapperkey = 'workbench-drag-wrapper';
    const drag = useRef();
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: -1, y: -1 });
    const [content, setContent] = useState(null);
    useEffect(() => {
        if (!document || !drag.current || !enable)
            return;
        const target = drag.current;
        const mousedown$ = fromEvent(target, 'mousedown', { capture });
        const mousemove$ = fromEvent(document, 'mousemove', { capture });
        const mouseup$ = fromEvent(document, 'mouseup', { capture });
        const mousedownSub = mousedown$.pipe(switchMap((start) => {
            setIsDragging(true);
            const targetRect = target.getBoundingClientRect();
            const initialPos = {
                x: start.clientX - targetRect.left + offsetX,
                y: start.clientY - targetRect.top + offsetY,
            };
            setPosition(initialPos);
            if (onDragStart)
                setActionPointEvent(onDragStart, start);
            const dragContent = onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart(initialPos);
            if (isPromise(dragContent))
                dragContent.then(val => val && setContent(val));
            else if (dragContent)
                setContent(dragContent);
            handleEvent(start);
            const mouseupSub = mouseup$.pipe(first()).subscribe((end) => {
                if (onDragEnd)
                    setActionPointEvent(onDragEnd, end);
                onDragEnd === null || onDragEnd === void 0 ? void 0 : onDragEnd(position);
                setIsDragging(false);
                handleEvent(end);
            });
            // 这里添加防抖会导致onDrag不执行
            return mousemove$.pipe(map((move) => {
                const x = move.clientX - initialPos.x + offsetX * 2;
                const y = move.clientY - initialPos.y + offsetY * 2;
                const pos = { x, y };
                return {
                    position: pos,
                    event: move,
                };
            }), takeUntil(mouseup$), finalize(() => mouseupSub.unsubscribe()));
        }), subscribeOn(animationFrameScheduler))
            .subscribe(({ position: pos, event, }) => {
            setPosition(pos);
            if (onDrag)
                setActionPointEvent(onDrag, event);
            onDrag === null || onDrag === void 0 ? void 0 : onDrag(pos);
            handleEvent(event);
        });
        return () => mousedownSub.unsubscribe();
    }, []);
    useEffect(() => {
        if (content) {
            const container = document.querySelector(`body > div#${wrapperkey}`);
            if (isDragging) {
                if (!container) {
                    const wrapper = document.createElement('div');
                    wrapper.id = wrapperkey;
                    document.body.appendChild(wrapper);
                }
                render(React.createElement(DragWrapper, { position: position, key: wrapperkey }, content), container);
            }
            else if (container) {
                unmountComponentAtNode(container);
                container.remove();
            }
        }
    }, [content, isDragging, position]);
    const refCallback = useCallback((node) => {
        if (!enable)
            return;
        if (node) {
            drag.current = node;
        }
    }, []);
    return {
        isDragging,
        position,
        refCallback,
    };
}

export { useDrag };
//# sourceMappingURL=index.js.map
